import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _get from "lodash/get";
import _map from "lodash/map";
import "core-js/modules/es6.array.slice.js";
// This class remains unchanged from from the legacy code.
// Refactoring it and the 'createPatternFormatter'
// usage of it below could provide some simplification and optimizations
//
// It takes a `formatPattern` string and provides methods
// to:
// - determine if a position in a string is/should be a 'delimiter'
// - retrieve the 'delimiter' for a
// - format a string to the given pattern restrictions
// - extract the 'raw' value from a formatted string
//
// formatPattern:
//  '9' is numeral
//  'a' is alpha
//  'x' is all (alphanumeric?)
//  any other character is a delimiter in the pattern
//
// example:
//  999 - 99 - 9999
//  3 numerals + ' - ' + 2 numerals + ' - ' + 4 numerals
//
//  const pattern = new FormatterPattern('999 - 99 - 9999');
//  pattern.format('123456789') --> '123 - 45 - 6789';
//  pattern.extract('123 - 45 - 6789') --> '123456789';
//
// See tests for more examples
export var FormatterPattern = /*#__PURE__*/function () {
  function FormatterPattern(formatPattern) {
    _classCallCheck(this, FormatterPattern);

    this.pattern = _map(formatPattern, function (char) {
      var test;
      var delimiter = false;

      switch (char) {
        case 'a':
          test = function test(val) {
            return /[A-Za-z]/.test(val);
          };

          break;

        case '9':
          test = function test(val) {
            return /[0-9]/.test(val);
          };

          break;

        case 'x':
          test = function test(val) {
            return /[A-Za-z0-9]/.test(val);
          };

          break;

        default:
          delimiter = true;

          test = function test(val) {
            return char === val;
          };

          break;
      }

      return {
        char: char,
        test: test,
        delimiter: delimiter
      };
    });
  }

  _createClass(FormatterPattern, [{
    key: "getItem",
    value: function getItem(caret) {
      return this.pattern[caret];
    } // process value into pattern, or extract raw value from patterned value

  }, {
    key: "processValue",
    value: function processValue(val) {
      var raw = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var chars = '';
      var item;
      var char;
      var trailingDelimiters = 0;
      var patternIdx = 0;
      var valIdx = 0; // now step through each character of the value and the pattern

      while (valIdx < val.length) {
        char = val[valIdx]; // current character in value

        item = this.getItem(patternIdx); // what character should be in the pattern

        if (item === undefined) {
          // index in val is greater than total length of pattern
          break;
        } else if (raw && item.delimiter) {
          // raw extraction: removing expected delimiters
          if (item.test(char)) {
            // current char is expected delimiter
            valIdx++; // step to next char in value
          }

          patternIdx++; // always step to next char in pattern
        } else if (item.test(char)) {
          // raw and patterned: is current character valid in the pattern?
          chars += char; // add current char to output

          trailingDelimiters = item.delimiter ? trailingDelimiters + 1 : 0; // keep track of trailing delimiters.  reset when not delimiter

          patternIdx++; // step to next char in pattern

          valIdx++; // step to next char in value
        } else if (item.delimiter) {
          // current idx in pattern is delimiter char
          chars += item.char; // add the matching delimiter to output

          trailingDelimiters++;
          patternIdx++; // step to next char in pattern
        } else {
          // current char in value is invalid
          valIdx++; // step to next char in value
        }
      }

      chars = chars.slice(0, chars.length - trailingDelimiters); // remove trailing delimiters from pattern format

      return chars;
    } // format a string to a pattern

  }, {
    key: "format",
    value: function format(val) {
      return this.processValue(val, false);
    } // return the raw value from a formatted pattern string

  }, {
    key: "extract",
    value: function extract(val) {
      return this.processValue(val, true);
    }
  }, {
    key: "isDelimiterPosition",
    value: function isDelimiterPosition(idx) {
      return _get(this.getItem(idx), 'delimiter', false);
    }
  }, {
    key: "isFollowingDelimiter",
    value: function isFollowingDelimiter(idx) {
      return this.isDelimiterPosition(idx - 1);
    }
  }]);

  return FormatterPattern;
}(); // formatPattern:
//  '9' is numeral
//  'a' is alpha
//  'x' is all (alphanumeric?)
//  any other character is a delimiter in the pattern
//
// Creates an object to hold and update the state of an input value
// Allows for selecting, inserting, and removing parts of the string
// currently being inputted.
//
// As characters are inserted or removed, the selection is updated in state
// so that the dom element being controlled can update its selected range to
// keep the user's inputting in sync with formatting applied as they type
//
// Additionally, as characters are inserted or removed, this state machine
// eagerly appends matching 'delimiter' characters in teh formatPattern.
//
// for example:
//   const formatter = createPatternFormatter('999 - 99 - 9999');
//   formatter.insert('123');
//   formatter.selection; -> { selectionStart: 6, selectionEnd: 6 }
//   formatter.value; -> '123 - '
//   formatter.raw; -> '123'
//
// see tests for additional usage scenarios

export function createPatternFormatter(formatPattern) {
  var pattern = new FormatterPattern(formatPattern);
  var value = ''; // the raw value formatted to the pattern

  var selectionStart = 0;
  var selectionEnd = 0;

  var clear = function clear() {
    value = '';
    selectionStart = 0;
    selectionEnd = 0;
  }; // deletion logic. allows deletion of trailing delimiters


  var removeSelection = function removeSelection() {
    // splice selection out of the string
    var inputValue = value.slice(0, selectionStart) + value.slice(selectionEnd, value.length); // format the string to the pattern

    value = pattern.format(inputValue); // convert input value to pattern
    // manage the caret
    // since delimiters aren't eagerly added, that means that
    // delimiters are eagerly deleted.
    // ensure selection is not past end of string

    selectionStart = Math.min(value.length, selectionStart); // update the selection to current caret position

    selectionEnd = selectionStart;
  }; // append logic.  adds trailing delimiters and advances caret


  var insert = function insert(chars) {
    if (!chars) return; // splice chars into string
    // this is for appending a char or pasting over middleof string

    var inputValue = value.slice(0, selectionStart) + chars + value.slice(selectionEnd, value.length); // format the string to the pattern

    value = pattern.format(inputValue); // convert input value to pattern
    // advance the caret

    selectionEnd = selectionStart + chars.length; // determine the amount of delimiters added with this entry

    var inputBeforeCaret = inputValue.slice(0, selectionEnd);
    var valueBeforeCaret = pattern.format(inputBeforeCaret);
    selectionEnd += valueBeforeCaret.length - inputBeforeCaret.length; // eagerly add trailing delimiter as indicator

    while (pattern.isDelimiterPosition(value.length)) {
      value += pattern.getItem(value.length).char;
    } // move the caret past trailing delimiters
    // multiple delimiters can exist in a row. follow them forward


    while (pattern.isDelimiterPosition(selectionEnd)) {
      selectionEnd += 1;
    } // update the selection to current caret position


    selectionStart = selectionEnd;
  }; // for when user pressed backspace


  var backspace = function backspace() {
    // if user pressed backspace without a range selected.
    if (selectionStart === selectionEnd) {
      // select previous char as range
      selectionStart = Math.max(0, selectionStart - 1);
    } // remove the current selection range


    removeSelection();
  }; // for when user holds meta and pushes backspace


  var deleteLineBackward = function deleteLineBackward() {
    // selection range: everything before selectionEnd
    selectionStart = 0; // remove the current selection range

    removeSelection();
  };

  return {
    clear: clear,
    insert: insert,
    backspace: backspace,
    deleteLineBackward: deleteLineBackward,
    setSelection: function setSelection(_ref) {
      var newSelectionStart = _ref.selectionStart,
          newSelectionEnd = _ref.selectionEnd;
      selectionStart = newSelectionStart;
      selectionEnd = newSelectionEnd;
    },

    get raw() {
      return pattern.extract(value);
    },

    get selection() {
      return {
        selectionStart: selectionStart,
        selectionEnd: selectionEnd
      };
    },

    get value() {
      return value;
    },

    format: function format(input) {
      return pattern.format(input);
    }
  };
}